import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Building2, Menu, X, Calculator, Phone } from 'lucide-react';

interface NavigationProps {
  isScrolled: boolean;
  isMenuOpen: boolean;
  setIsMenuOpen: (isOpen: boolean) => void;
}

export function Navigation({ isScrolled, isMenuOpen, setIsMenuOpen }: NavigationProps) {
  const location = useLocation();

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const mobileMenu = document.getElementById('mobile-menu');
      const menuButton = document.getElementById('menu-button');
      
      if (mobileMenu && menuButton && 
          !mobileMenu.contains(event.target as Node) && 
          !menuButton.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [setIsMenuOpen]);

  // Close menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location, setIsMenuOpen]);

  return (
    <>
      <nav 
        className={`fixed w-full z-50 transition-all duration-500 ${
          isScrolled ? 'py-2 sm:py-4 bg-zinc-900/95 backdrop-blur-sm' : 'py-4 sm:py-6'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 sm:gap-3">
            <Building2 className="h-6 w-6 sm:h-8 sm:w-8 text-yellow-400" />
            <span className="text-xl sm:text-2xl font-light tracking-wider text-zinc-100">
              GOLDEN <span className="font-medium">TOUCH</span>
            </span>
          </Link>

          <div className="flex items-center gap-3 sm:gap-6">
          <a
            href="tel:9149606433"
            className="inline-flex items-center gap-2 bg-yellow-400 text-zinc-900 px-3 py-2 text-sm font-medium"
          >
            <Phone className="h-4 w-4" />
            <span className="hidden sm:inline">(914) 960-6433</span>
            <span className="sm:hidden">Call</span>
          </a>

          <div className="hidden md:flex items-center gap-8 lg:gap-12">
            <div className="flex items-center gap-8 lg:gap-12">
              <Link to="/services" className="nav-link text-sm tracking-wider">Services</Link>
              <Link to="/about" className="nav-link text-sm tracking-wider">About</Link>
              <Link to="/blog" className="nav-link text-sm tracking-wider">Blog</Link>
              <Link to="/project-planner" className="nav-link text-sm tracking-wider flex items-center gap-2">
                <Calculator className="h-4 w-4" />
                Project Planner
              </Link>
              <Link to="/contact" className="nav-link text-sm tracking-wider">Contact</Link>
            </div>
            <Link 
              to="/consultation" 
              className="px-6 py-2 border border-yellow-400 text-yellow-400 text-sm tracking-wider hover:bg-yellow-400 hover:text-zinc-900 transition-all duration-300"
            >
              Get Estimate
            </Link>
          </div>

          <button 
            id="menu-button"
            className="md:hidden p-2 -mr-2 z-50" 
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? (
              <X className="h-6 w-6 text-zinc-200" />
            ) : (
              <Menu className="h-6 w-6 text-zinc-200" />
            )}
          </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div 
        id="mobile-menu"
        className={`md:hidden fixed inset-0 bg-zinc-900/95 backdrop-blur-sm z-40 transition-all duration-300 ${
          isMenuOpen 
            ? 'opacity-100 pointer-events-auto translate-y-0' 
            : 'opacity-0 pointer-events-none -translate-y-full'
        }`}
        style={{ top: '60px' }}
      >
        <div className="px-4 py-6 space-y-4">
          <a
            href="tel:9149606433"
            className="flex items-center justify-center gap-2 w-full px-6 py-3 bg-yellow-400 text-zinc-900 text-lg font-medium"
            onClick={() => setIsMenuOpen(false)}
          >
            <Phone className="h-5 w-5" />
            (914) 960-6433
          </a>
          <Link 
            to="/services" 
            className="block text-lg font-light hover:text-yellow-400 transition-colors" 
            onClick={() => setIsMenuOpen(false)}
          >
            Services
          </Link>
          <Link 
            to="/about" 
            className="block text-lg font-light hover:text-yellow-400 transition-colors" 
            onClick={() => setIsMenuOpen(false)}
          >
            About
          </Link>
          <Link 
            to="/blog" 
            className="block text-lg font-light hover:text-yellow-400 transition-colors" 
            onClick={() => setIsMenuOpen(false)}
          >
            Blog
          </Link>
          <Link 
            to="/project-planner" 
            className="block text-lg font-light hover:text-yellow-400 transition-colors flex items-center gap-2" 
            onClick={() => setIsMenuOpen(false)}
          >
            <Calculator className="h-5 w-5" />
            Project Planner
          </Link>
          <Link 
            to="/contact" 
            className="block text-lg font-light hover:text-yellow-400 transition-colors" 
            onClick={() => setIsMenuOpen(false)}
          >
            Contact
          </Link>
          <div className="pt-2">
            <Link 
              to="/consultation" 
              className="block w-full px-6 py-3 border border-yellow-400 text-yellow-400 text-center text-lg font-light hover:bg-yellow-400 hover:text-zinc-900 transition-all duration-300"
              onClick={() => setIsMenuOpen(false)}
            >
              Get Estimate
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}